import { useCallback, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import { logError } from "@entrilia/shared/logging.ts";
import type { ChangeReportGroupRequest, Report } from "../../../../../../api/types/svTypes.ts";
import type { FromReport } from "./FromReport.ts";
import ReportGroupsConnector from "./ReportGroupsConnector.tsx";
import SaveReportFormWrapper from "./SaveReportFormWrapper.tsx";

interface Props {
  report: FromReport;
  changeGroup: (report: ChangeReportGroupRequest) => Promise<Report | undefined>;
  onChanged: (report: Report) => void;
  onClose: () => void;
}

export default function ChangeReportGroup({ report, changeGroup, onChanged, onClose }: Props) {
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const onSaveClicked = useCallback(
    async (_: unknown, groupId: string) => {
      setSaving(true);
      try {
        const resp = await changeGroup({ reportId: report.reportId, groupId, clientCode: report.clientCode });
        if (resp === undefined) {
          setError("An error occurred while changing the group.");
        } else {
          onChanged(resp);
          onClose();
        }
      } catch (e) {
        logError(e, "[ChangeReportGroup] changeGroup");
        setError("An error occurred while changing the group.");
      } finally {
        setSaving(false);
      }
    },
    [changeGroup, report.reportId, report.clientCode, onChanged, onClose]
  );

  return (
    <PromptDialog open={true}>
      <ReportGroupsConnector clientCode={report.clientCode}>
        <SaveReportFormWrapper
          report={report}
          error={error}
          title="Move Report to Group"
          saveBtnTitle="Save"
          saving={saving}
          showLevel
          onClose={onClose}
          onSave={onSaveClicked}
        />
      </ReportGroupsConnector>
    </PromptDialog>
  );
}
